import React, { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import Buttons from '@components/Buttons';
import HandCoins from '@components/Icons/HandCoins';
import CheckCircle from '@components/Icons/CheckCircle';
import Clock from '@components/Icons/Clock';
import { useRouter } from 'next/router';
import { Invoice, INVOICE_STATUS, Price, Product } from '@typings/api-models';
import { ProductType } from '@typings/graphql-models';
import AttachPriceToInvoiceModal from '@modals/AttachPriceToInvoiceModal';
import { useUser } from '@hooks';
import s from './InvoiceCard.module.scss';

type Props = {
  invoice: Pick<Invoice, 'id' | 'status' | 'fallbackTitle' | 'personId' | 'title'>;
  price?: Pick<Price, 'id' | 'priceTitle'> & {
    product?:
      | (Pick<Product, 'id' | 'title'> & {
          productTypes?: Pick<ProductType, 'noodleProductType'>[];
        })
      | null;
  } | null;
  canClick: boolean;
  creatorSlug: string;
  reload?: () => Promise<void>;
  setIsFulfilled?: (f: boolean) => void;
};

const InvoiceCard: FC<Props> = ({ setIsFulfilled, invoice, price, canClick, creatorSlug, reload }) => {
  const router = useRouter();

  const [isModalOpen, setModalOpen] = useState(false);
  const [user] = useUser();

  const isPaid = invoice.status === INVOICE_STATUS.PAID;
  const isInitiated = invoice.status === INVOICE_STATUS.PAYMENT_INITIATED || invoice.status === INVOICE_STATUS.PAYMENT_REQUESTED;
  const isGenerating = invoice.status === INVOICE_STATUS.IS_GENERATING;
  const isCustomer = user?.id === invoice.personId;

  useEffect(() => {
    setIsFulfilled?.(isPaid || isInitiated);
  }, [isPaid, isInitiated, setIsFulfilled]);

  const title = invoice.title || price?.priceTitle || price?.product?.title;

  const handleClick = (): void => {
    if (isGenerating) {
      setModalOpen(true);
    } else {
      router.push(
        getUrl(IDENTIFIERS.INVOICE_DETAIL, {
          creatorSlug,
          invoiceId: invoice.id,
        }),
      );
    }
  };

  const getGeneratingTitleText = (): string => {
    let fallbackTitleText = '';
    if (invoice.fallbackTitle) {
      fallbackTitleText = ` ${invoice.fallbackTitle}`;
    }
    if (isCustomer) {
      return `Your${fallbackTitleText} invoice is being generated`;
    }
    return `Generate${fallbackTitleText} invoice`;
  };

  return (
    <>
      <Buttons
        disabled={!canClick}
        className={s.container}
        isFullWidth
        isWrapper
        onClick={handleClick}
      >
        <figure style={(isPaid && { backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }) || {}}>
          <HandCoins weight="fill" color={isPaid ? 'var(--color-gray-0)' : 'var(--color-primary)'} size={24} />
          <div className={s.complete}>
            {isPaid && (
              <CheckCircle size={16} weight="fill" color="var(--color-success)" />
            )}
            {(isInitiated || isGenerating) && (
              <Clock size={16} weight="fill" color="var(--color-gray-100)" />
            )}
          </div>
        </figure>
        {!isGenerating && (
          <div className={s.paymentText}>
            {!isInitiated && <p className='caption'>Invoice request</p>}
            <p className="body-sm-bold">Pay invoice for {title}</p>
            {isInitiated && <p className='caption'>This payment is pending.</p>}
          </div>
        )}
        {isGenerating && (
          <div className={classNames(s.isGenerating, s.paymentText)}>
            <p className='body-sm-bold'>{getGeneratingTitleText()}</p>
            {canClick && <p className='caption'>Click here to generate</p>}
          </div>
        )}
        {canClick && <Buttons isExtraSmall isThird className={s.open}>View</Buttons>}
      </Buttons>
      {isModalOpen && <AttachPriceToInvoiceModal
        invoiceId={invoice.id}
        onClose={async () => {
          if (reload) {
            await reload();
          }
          setModalOpen(false);
        }}
      />}
    </>
  );
};

export default InvoiceCard;
